// BRK-S400 — THE STATE MODEL OF A BOND SUBMISSION (proposal §10), as the broker
// order view will publish it.
//
// Three parallel lifecycles, each its own block, and the portal RENDERS them —
// it never derives one from another (CLAUDE.md #7). `workflowStatus`, which
// conflated them, left the broker contract with PMS-S837: it is PMS's internal
// column, and `status` is its broker projection.
//
//   • the SUBMISSION's status (`status`, with `closeReason`) and two facts
//     beside it: the bid result and a pending change request;
//   • each BOND's status (`bonds[]`), one row per bond on the submission;
//   • the JOB's phase (`lifecycle`): bid → award → performance period →
//     maintenance → closed.
//
// PMS-E042 publishes every block here (PMS-S836..S859); BRK built them first,
// consumer-first, and BRK-S511 re-pinned onto `epic/PMS-E042`, so each note
// below names the PMS story that publishes its field. Every vocabulary is the
// one PMS publishes, and every reader below tolerates absence and an unknown
// member the BRK-S114 way: a neutral label, never a blank and never a throw.

// ── The submission (§10.1) ───────────────────────────────────────────────────

/** The pre-v2 statuses; `cancelled` survives as a discarded draft (D14). */
export const SUBMISSION_STATUSES_PINNED = [
  "draft",
  "submitted",
  "cancelled",
] as const;

/**
 * The v2 submission status, verbatim from §10.1. PMS-S837 (P2) publishes
 * these nine plus `cancelled`.
 */
export const SUBMISSION_STATUSES_V2 = [
  "draft",
  "submitted",
  "underwriter_review",
  "awaiting_capacity",
  "approved",
  "pp_requested",
  "withdrawn",
  "declined",
  "closed",
] as const;

/** Every status PMS publishes: the v2 set, plus `cancelled` for a discarded
 *  draft (D14). */
export type BondSubmissionStatus =
  | (typeof SUBMISSION_STATUSES_PINNED)[number]
  | (typeof SUBMISSION_STATUSES_V2)[number];

const SUBMISSION_STATUS_LABELS: Record<BondSubmissionStatus, string> = {
  draft: "Draft",
  submitted: "Filed with Signal",
  // Decision 8 (workflowStatus.ts): no underwriting vocabulary on a broker
  // screen. The wire value is unchanged.
  underwriter_review: "In review",
  awaiting_capacity: "Awaiting capacity",
  approved: "Approved",
  pp_requested: "P&P requested",
  withdrawn: "Withdrawn",
  declined: "Declined",
  closed: "Closed",
  // A discarded draft (BRK-S181). A broker's withdrawal is `withdrawn` since
  // BRK-S502; nothing spells it `cancelled` any more.
  cancelled: "Cancelled",
};

/** A submission status, labelled; one this build has not adopted reads "In
 *  progress" — the same neutral word `workflowStatusLabel` uses. */
export function submissionStatusLabel(raw: unknown): string {
  return typeof raw === "string" && Object.hasOwn(SUBMISSION_STATUS_LABELS, raw)
    ? SUBMISSION_STATUS_LABELS[raw as BondSubmissionStatus]
    : "In progress";
}

/** The statuses of an order filed with Signal and not withdrawn, declined or
 *  cancelled (BRK-S432): the five live ones and `closed`, where a completed
 *  job and a lost bid both end. */
const FILED_STATUSES: readonly string[] = [
  "submitted",
  "underwriter_review",
  "awaiting_capacity",
  "approved",
  "pp_requested",
  "closed",
];

/**
 * Filed with Signal (BRK-S432): `submitted` and every v2 status that follows
 * it, so an `approved` or `underwriter_review` order behaves as `submitted`
 * did. Withdrawn, declined and the pinned `cancelled` are NOT filed — the
 * request was taken back or refused. A status this build has not adopted is
 * not filed either: a gate that grants an action on a value nobody named would
 * be offering what the server did not (CLAUDE.md #7). The record of a filed
 * order reads `isPastDraftStatus` instead.
 */
export function isFiledStatus(status: unknown): boolean {
  return typeof status === "string" && FILED_STATUSES.includes(status);
}

/**
 * Past draft: every status but `draft` (BRK-S399). This is the order's
 * RECORD — its timeline says it was filed, and the wizard never reopens it as
 * a form — so a withdrawn, declined or unknown status counts too: only a filed
 * order earns one, and an unknown one must never re-open the Resume path.
 */
export function isPastDraftStatus(status: unknown): boolean {
  return typeof status === "string" && status !== "draft";
}

/** The statuses after which nothing more happens on the request: withdrawn,
 *  a discarded draft (`cancelled`), declined, closed. */
const ENDED_STATUSES: readonly string[] = [
  "withdrawn",
  "cancelled",
  "declined",
  "closed",
];

/** Whether the request has ended (BRK-S399). An unknown status has not: it
 *  was not named as an end, so nothing is taken away on its account. */
export function isEndedStatus(raw: unknown): boolean {
  return typeof raw === "string" && ENDED_STATUSES.includes(raw);
}

/** Why a submission closed. PMS-S837 (P2) — `closeReason`. */
export const BOND_CLOSE_REASONS = [
  "not_awarded",
  "completed",
  "released",
  "cancelled",
] as const;
export type BondCloseReason = (typeof BOND_CLOSE_REASONS)[number];

const CLOSE_REASON_LABELS: Record<BondCloseReason, string> = {
  not_awarded: "Not awarded",
  completed: "Completed",
  released: "Released",
  cancelled: "Cancelled",
};

/** A close reason, labelled — or null for none, or one not adopted (the
 *  status already says "Closed", so nothing is lost by saying no more). */
export function closeReasonLabel(raw: unknown): string | null {
  return typeof raw === "string" && Object.hasOwn(CLOSE_REASON_LABELS, raw)
    ? CLOSE_REASON_LABELS[raw as BondCloseReason]
    : null;
}

// ── The header pill (D17) ───────────────────────────────────────────────────

/**
 * The record's header pill, SERVED (PMS-S837, D17): PMS applies the rule the
 * portal used to (`submissionHeaderPills`, deleted in BRK-S511) and the portal
 * labels the value. CLOSED.
 */
export const BOND_HEADER_STATUSES = [
  "draft",
  "submitted",
  "bid_bond_issued",
  "awarded",
  "bonded",
  "maintenance",
  "closed",
  "cancelled",
  "complete",
] as const;
export type BondHeaderStatus = (typeof BOND_HEADER_STATUSES)[number];

/** A served header status, or null for one this build has not adopted. */
export function readHeaderStatus(raw: unknown): BondHeaderStatus | null {
  return member(BOND_HEADER_STATUSES, raw);
}

// ── The two facts beside the status ──────────────────────────────────────────

/** The bid result as a fact (§10.1): `none` is the absent block. */
export const BID_RESULT_FACTS = ["none", "low_bidder", "won", "lost"] as const;
export type BidResultFact = (typeof BID_RESULT_FACTS)[number];

/**
 * Where the bid stands. `null` — not `none` — for an outcome this build has
 * not adopted: a result IS on file, and calling it "none" would offer the
 * broker a report that already exists.
 */
export function bidResultFact(order: {
  bidResult?: { outcome?: unknown } | null;
}): BidResultFact | null {
  if (order.bidResult == null) return "none";
  const outcome = order.bidResult.outcome;
  return outcome === "low_bidder" || outcome === "won" || outcome === "lost"
    ? outcome
    : null;
}

export const BOND_CHANGE_REQUEST_FACTS = [
  "none",
  "edit_pending",
  "rider_pending",
] as const;
export type BondChangeRequestFact = (typeof BOND_CHANGE_REQUEST_FACTS)[number];

/** One field's pending change: what the bond says now, and what was asked. */
export interface BondChangeRequestField {
  current: unknown;
  requested: unknown;
}

/**
 * A change awaiting underwriting — an edit before award, a rider after.
 * Null is no pending change.
 *
 * PMS-S837 (P2) — `changeRequest`; its writers are PMS-S845 (edit) and
 * PMS-S850 (rider). `fields` is keyed by the same configuration paths the
 * captured values are, so a value inside is a captured-value representation
 * (never a formatted string, D-10).
 */
export interface BondChangeRequest {
  kind: "edit" | "rider";
  status: "pending";
  /** The change's own id — what a rider's withdraw addresses. REQUIRED
   *  (PMS-S850): a rider the portal cannot address cannot be withdrawn. */
  id: string;
  fields: Record<string, BondChangeRequestField>;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function readChangeRequest(raw: unknown): BondChangeRequest | null {
  if (!isRecord(raw)) return null;
  if (raw.kind !== "edit" && raw.kind !== "rider") return null;
  if (raw.status !== "pending") return null;
  const id = text(raw.id);
  if (id === null) return null;
  const fields: Record<string, BondChangeRequestField> = {};
  if (isRecord(raw.fields)) {
    for (const [key, pair] of Object.entries(raw.fields)) {
      // PMS publishes both halves as optional (an unknown value), so an
      // absent half reads as null — cleared — never as a dropped field.
      if (isRecord(pair)) {
        fields[key] = {
          current: pair.current ?? null,
          requested: pair.requested ?? null,
        };
      }
    }
  }
  return { kind: raw.kind, status: "pending", id, fields };
}

/**
 * The fields an APPROVED edit or rider changed, so Project Details can mark
 * them "Updated". PMS-S857 — `recentChanges`, null once no longer recent
 * (30 days after approval). Served, never diffed: the SPA cannot tell a
 * changed value from one that was always so (#7).
 */
export interface BondRecentChanges {
  fields: string[];
  approvedAt: string | null;
}

/** Tolerant: no list of fields reads as none; a non-string key is dropped. */
export function readRecentChanges(raw: unknown): BondRecentChanges | null {
  if (!isRecord(raw) || !Array.isArray(raw.fields)) return null;
  return {
    fields: (raw.fields as unknown[]).filter(
      (f): f is string => typeof f === "string" && f !== "",
    ),
    approvedAt: text(raw.approvedAt),
  };
}

/** A field's change marker, as `BondDetailsFields` draws it. */
export type BondChangeMarker = "pending" | "updated";

/**
 * The markers an order's served facts put on its fields: "pending" for each
 * field the pending `changeRequest` names (with what the bond carries now),
 * "updated" for each field `recentChanges` names. A field still pending shows
 * only the pending marker. Nothing is compared here.
 */
export function bondFieldMarkers(order: {
  changeRequest?: unknown;
  recentChanges?: unknown;
}): {
  markers: Record<string, BondChangeMarker>;
  onBond: Record<string, unknown>;
} {
  const markers: Record<string, BondChangeMarker> = {};
  const onBond: Record<string, unknown> = {};
  for (const key of readRecentChanges(order.recentChanges)?.fields ?? []) {
    markers[key] = "updated";
  }
  const pending = readChangeRequest(order.changeRequest)?.fields ?? {};
  for (const [key, change] of Object.entries(pending)) {
    markers[key] = "pending";
    onBond[key] = change.current;
  }
  return { markers, onBond };
}

/**
 * The pending-change fact. `null` for a block this build cannot read: there is
 * SOMETHING pending, so `none` would be a false all-clear.
 */
export function changeRequestFact(order: {
  changeRequest?: unknown;
}): BondChangeRequestFact | null {
  if (order.changeRequest == null) return "none";
  const read = readChangeRequest(order.changeRequest);
  if (read === null) return null;
  return read.kind === "edit" ? "edit_pending" : "rider_pending";
}

// ── The bonds (§10.2) ────────────────────────────────────────────────────────

/** PMS-S836 (P1) — `bonds[].kind`. A rider is an amendment of a bond
 *  (`pendingRider`), never a bond (D16). */
export const BOND_KINDS = [
  "bid",
  "performance",
  "payment",
  "supply",
  "maintenance",
] as const;
export type BondKind = (typeof BOND_KINDS)[number];

/** PMS-S836 (P1) — `bonds[].status`. */
export const BOND_STATUSES = [
  "not_requested",
  "requested",
  "in_underwriting",
  "approved",
  "issued",
  "voided",
  "converting",
  "released",
  "exonerated",
  "cancelled",
] as const;
export type BondStatus = (typeof BOND_STATUSES)[number];

/**
 * A rider waiting on underwriting against THIS bond (PMS-S836 / PMS-S850):
 * its id (the order's `changeRequest.id`), its rider type and when it was
 * asked. The pending values live on the order's `changeRequest`. `type` is an
 * open string on the wire; `riderTypeLabel` labels it.
 */
export interface BondPendingRider {
  id: string;
  type: string;
  requestedAt: string;
}

/**
 * One bond on the submission. PMS-S836 (P1) — `bonds[]`.
 *
 * Money is a plain number and dates ISO `YYYY-MM-DD` on the wire (D-10).
 * `basis` is the SERVER's sentence for how the penal sum was set ("100% of
 * contract", "5% of bid") — display copy, not a vocabulary to branch on.
 * `bondNumber`, `effectiveDate` and the money are null until the bond exists.
 */
export interface BondRow {
  id: string;
  kind: BondKind;
  bondNumber: string | null;
  formTitle: string | null;
  penalSum: number | null;
  basis: string | null;
  contractPrice: number | null;
  effectiveDate: string | null;
  status: BondStatus;
  pendingRider: BondPendingRider | null;
  /** The executed bond form, downloaded through the ACCOUNT's document route;
   *  null until the bond issues (BRK-S441, PMS-S836). */
  documentId: string | null;
}

/** A bond row as read: a kind or status this build has not adopted is null
 *  rather than dropping the row — a record of a bond is never hidden. */
export interface BondRowView extends Omit<BondRow, "kind" | "status"> {
  kind: BondKind | null;
  status: BondStatus | null;
}

const text = (v: unknown): string | null =>
  typeof v === "string" && v.trim() !== "" ? v : null;
const money = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;
const member = <T extends string>(list: readonly T[], v: unknown): T | null =>
  list.find((m) => m === v) ?? null;

/** A pending rider is addressable only with its id and date; a block missing
 *  either reads as none rather than a rider the portal cannot withdraw. */
function readPendingRider(raw: unknown): BondPendingRider | null {
  if (!isRecord(raw)) return null;
  const id = text(raw.id);
  const requestedAt = text(raw.requestedAt);
  if (id === null || requestedAt === null) return null;
  return {
    id,
    type: typeof raw.type === "string" ? raw.type : "",
    requestedAt,
  };
}

export function readBondRows(raw: unknown): BondRowView[] {
  if (!Array.isArray(raw)) return [];
  const rows: BondRowView[] = [];
  for (const item of raw as unknown[]) {
    if (!isRecord(item)) continue;
    const id = text(item.id);
    if (id === null) continue;
    const rider = readPendingRider(item.pendingRider);
    rows.push({
      id,
      kind: member(BOND_KINDS, item.kind),
      bondNumber: text(item.bondNumber),
      formTitle: text(item.formTitle),
      // A formatted string is never accepted from the wire (D-10): it reads
      // as absent, not as a number somebody parsed.
      penalSum: money(item.penalSum),
      basis: text(item.basis),
      contractPrice: money(item.contractPrice),
      effectiveDate: text(item.effectiveDate),
      status: member(BOND_STATUSES, item.status),
      pendingRider: rider,
      documentId: text(item.documentId),
    });
  }
  return rows;
}

const BOND_KIND_LABELS: Record<BondKind, string> = {
  bid: "Bid bond",
  performance: "Performance bond",
  payment: "Payment bond",
  supply: "Supply bond",
  maintenance: "Maintenance bond",
};

export function bondKindLabel(kind: BondKind | null): string {
  return kind === null ? "Bond" : BOND_KIND_LABELS[kind];
}

const BOND_STATUS_LABELS: Record<BondStatus, string> = {
  not_requested: "Not requested",
  requested: "Requested",
  in_underwriting: "In review",
  // §10.2: approved, issuance being prepared or out for signature.
  approved: "Waiting on Bond",
  issued: "Issued / Active",
  voided: "Voided",
  converting: "Converting to final",
  released: "Released",
  exonerated: "Exonerated",
  cancelled: "Cancelled",
};

export function bondStatusLabel(status: BondStatus | null): string {
  return status === null ? "Status unavailable" : BOND_STATUS_LABELS[status];
}

// ── The job's phase (§10.3) ──────────────────────────────────────────────────

/** PMS-S837 (P2) — `lifecycle.phase`, in lifecycle order. */
export const BOND_PHASE_KEYS = [
  "bid",
  "award",
  "performance_period",
  "maintenance",
  "closed",
] as const;
export type BondPhaseKey = (typeof BOND_PHASE_KEYS)[number];

/** PMS-S837 (P2) — `lifecycle.phases[].state`. */
export const BOND_PHASE_STATES = [
  "done",
  "current",
  "upcoming",
  "skipped",
  "stopped",
] as const;
export type BondPhaseState = (typeof BOND_PHASE_STATES)[number];

/** One phase: its state, the date it reached it (ISO, or null) and PMS's
 *  short sub-label ("Awaiting bid results", "Warranty through 4/30/2027"). */
export interface BondLifecyclePhase {
  phase: BondPhaseKey;
  state: BondPhaseState;
  at: string | null;
  note: string | null;
}

/** PMS-S837 (P2) — `lifecycle`. */
export interface BondLifecycle {
  phase: BondPhaseKey;
  phases: BondLifecyclePhase[];
}

export interface BondLifecycleView {
  /** null for a current phase this build has not adopted. */
  phase: BondPhaseKey | null;
  /** The phases this build can place, in canonical order; a state it has not
   *  adopted is null and renders neutrally. */
  phases: (Omit<BondLifecyclePhase, "state"> & {
    state: BondPhaseState | null;
  })[];
}

export function readBondLifecycle(raw: unknown): BondLifecycleView | null {
  if (!isRecord(raw) || !Array.isArray(raw.phases)) return null;
  const byKey = new Map<BondPhaseKey, BondLifecycleView["phases"][number]>();
  for (const item of raw.phases as unknown[]) {
    if (!isRecord(item)) continue;
    const phase = member(BOND_PHASE_KEYS, item.phase);
    if (phase === null || byKey.has(phase)) continue;
    byKey.set(phase, {
      phase,
      state: member(BOND_PHASE_STATES, item.state),
      at: text(item.at),
      note: text(item.note),
    });
  }
  return {
    phase: member(BOND_PHASE_KEYS, raw.phase),
    phases: BOND_PHASE_KEYS.flatMap((k) => {
      const found = byKey.get(k);
      return found ? [found] : [];
    }),
  };
}

const PHASE_LABELS: Record<BondPhaseKey, string> = {
  bid: "Bid bond",
  award: "Award",
  performance_period: "Performance Bond Period",
  maintenance: "Maintenance",
  closed: "Closed",
};

export function phaseLabel(phase: BondPhaseKey): string {
  return PHASE_LABELS[phase];
}

// ── What the broker may do now ───────────────────────────────────────────────

/** PMS-S837 (P2) — `actions`. One fixed set; each is enabled or disabled with
 *  a broker-safe reason. `withdraw.enabled` equals `canWithdraw` (D9). */
export const BOND_ACTION_KEYS = [
  "bidResult",
  "edit",
  "rider",
  "docs",
  "message",
  "claim",
  "withdraw",
] as const;
export type BondActionKey = (typeof BOND_ACTION_KEYS)[number];

export interface BondAction {
  enabled: boolean;
  reason: string | null;
}

export type BondActions = Record<BondActionKey, BondAction>;

/**
 * The actions block, or null when PMS published none (an older PMS — the
 * caller keeps today's per-field fallbacks such as `canWithdraw`). An action
 * missing from a published block reads as DISABLED with no reason: offering a
 * button the server did not grant would be a false success waiting to happen.
 */
export function readBondActions(raw: unknown): BondActions | null {
  if (!isRecord(raw)) return null;
  const read = {} as BondActions;
  for (const key of BOND_ACTION_KEYS) {
    const a = raw[key];
    read[key] =
      isRecord(a) && typeof a.enabled === "boolean"
        ? { enabled: a.enabled, reason: text(a.reason) }
        : { enabled: false, reason: null };
  }
  return read;
}

/** PMS-S837 (P2) — `editPolicy.mode`. */
export const BOND_EDIT_MODES = ["direct", "rider", "none"] as const;
export type BondEditMode = (typeof BOND_EDIT_MODES)[number];

/** How the submission's details may change now, and which fields are locked
 *  (field key → the broker-safe reason). PMS-S837 (P2). */
export interface BondEditPolicy {
  mode: BondEditMode;
  locks: Record<string, string>;
}

/** An unknown mode reads as `none`: an edit surface the server did not name
 *  must not open. */
export function readEditPolicy(raw: unknown): BondEditPolicy | null {
  if (!isRecord(raw)) return null;
  const locks: Record<string, string> = {};
  if (isRecord(raw.locks)) {
    for (const [key, reason] of Object.entries(raw.locks)) {
      const r = text(reason);
      if (r !== null) locks[key] = r;
    }
  }
  return { mode: member(BOND_EDIT_MODES, raw.mode) ?? "none", locks };
}

/**
 * This bond against the program, as PMS computed it (PMS-S841, D18): WHICH
 * limit is over, by how much (`overBy`) and against what figure
 * (`limitAmount`) — both plain numbers, null when nothing is over. The portal
 * formats them and computes nothing. `hardOver` is over the program maximum
 * (`program.maxSingleLimit`), where no upgrade helps.
 */
export interface BondCapacityCheck {
  withinLimits: boolean;
  overLimit: "single" | "aggregate" | null;
  hardOver: boolean;
  overBy: number | null;
  limitAmount: number | null;
}

export function readCapacityCheck(raw: unknown): BondCapacityCheck | null {
  if (!isRecord(raw) || typeof raw.withinLimits !== "boolean") return null;
  return {
    withinLimits: raw.withinLimits,
    overLimit: member(["single", "aggregate"] as const, raw.overLimit),
    hardOver: raw.hardOver === true,
    // A formatted string is never accepted from the wire (D-10).
    overBy: money(raw.overBy),
    limitAmount: money(raw.limitAmount),
  };
}

// ── History events ───────────────────────────────────────────────────────────

/** A history row that is an EVENT rather than a status transition (§7 of the
 *  lifecycle reference). CLOSED. PMS-S841 (P6) — `history[].event`, null on
 *  a status move. */
export const BOND_HISTORY_EVENTS = [
  "gia_sent",
  "gia_resent",
  "gia_reminder_sent",
  "gia_signed",
  "program_changed",
  "capacity_review",
  "document_uploaded",
  "message_sent",
  "rider_requested",
  "rider_withdrawn",
  "edit_submitted",
  "bid_bond_voided",
  // BRK-S428 — a claim reported against one of the submission's issued bonds
  // (PMS-S851, the claim intake's history row).
  "claim_reported",
] as const;
export type BondHistoryEvent = (typeof BOND_HISTORY_EVENTS)[number];

export function readHistoryEvent(raw: unknown): BondHistoryEvent | null {
  return member(BOND_HISTORY_EVENTS, raw);
}

/** The History tab's Event column (BRK-S427): a sentence per event, in the
 *  wireframe's words. `capacity_review` says only that the submission went to
 *  review — never why, nor a score, tier, limit or figure (plan rule 25). */
const HISTORY_EVENT_LABELS: Record<BondHistoryEvent, string> = {
  gia_sent: "GIA Form sent to the indemnitors for signature",
  gia_resent: "GIA Form resent to the indemnitors for signature",
  gia_reminder_sent:
    "GIA Form reminder sent to the indemnitors who haven’t signed",
  gia_signed: "GIA Form signed by all indemnitors",
  program_changed: "Program enrollment changed",
  // Decision 8 (BRK-S502): "review", never underwriting.
  capacity_review: "Sent for review",
  document_uploaded: "Document uploaded",
  message_sent: "Message sent",
  rider_requested: "Rider request submitted",
  rider_withdrawn: "Rider request withdrawn",
  edit_submitted: "Submission updated",
  bid_bond_voided: "Bid bond voided",
  claim_reported: "Claim reported",
};

/** An event, labelled; an unknown one still reads as something happened. */
export function historyEventLabel(event: BondHistoryEvent | null): string {
  return event === null ? "Update" : HISTORY_EVENT_LABELS[event];
}

// ── Documents (PMS-S843) and GIA signers (PMS-S839) ──────────────────────────

/** PMS-S843 (P8) — a document row's `status`. D23 dropped `under_review`:
 *  review happens on the action item, never on the file. */
export const BOND_DOCUMENT_STATUSES = [
  "required",
  "received",
  "issued",
  "released",
] as const;
export type BondDocumentStatus = (typeof BOND_DOCUMENT_STATUSES)[number];

/** PMS-S843 (P8) — who supplied a document. `signal_request` is a placeholder
 *  Signal asked for (an open action item) and nobody has uploaded yet. */
export const BOND_DOCUMENT_SOURCES = [
  "broker",
  "signal",
  "obligee",
  "signal_request",
] as const;
export type BondDocumentSource = (typeof BOND_DOCUMENT_SOURCES)[number];

/** PMS-S843 (P8) — a submission document's `category`, the
 *  Submission documents table's Category column (BRK-S500). */
export const BOND_DOCUMENT_CATEGORIES = [
  "financials",
  "application",
  "project",
  "bond",
  "correspondence",
  "rider",
] as const;
export type BondDocumentCategory = (typeof BOND_DOCUMENT_CATEGORIES)[number];

const DOCUMENT_CATEGORY_LABELS: Record<BondDocumentCategory, string> = {
  financials: "Financials",
  application: "Application",
  project: "Project",
  bond: "Bond",
  correspondence: "Correspondence",
  rider: "Rider",
};

/** A document's category, labelled; one this build has not adopted reads
 *  "Other" — never a blank. */
export function documentCategoryLabel(raw: unknown): string {
  const c = member(BOND_DOCUMENT_CATEGORIES, raw);
  return c === null ? "Other" : DOCUMENT_CATEGORY_LABELS[c];
}

const DOCUMENT_STATUS_LABELS: Record<BondDocumentStatus, string> = {
  required: "Required",
  received: "Received",
  issued: "Issued",
  released: "Released",
};

export function bondDocumentStatusLabel(raw: unknown): string {
  const s = member(BOND_DOCUMENT_STATUSES, raw);
  return s === null ? "Status unavailable" : DOCUMENT_STATUS_LABELS[s];
}

const DOCUMENT_SOURCE_LABELS: Record<BondDocumentSource, string> = {
  broker: "You",
  signal: "Signal",
  obligee: "Obligee",
  signal_request: "Signal request",
};

export function documentSourceLabel(raw: unknown): string {
  const s = member(BOND_DOCUMENT_SOURCES, raw);
  return s === null ? "Other" : DOCUMENT_SOURCE_LABELS[s];
}

/** PMS-S839 (P4) — a GIA signer's own `status`. */
export const GIA_SIGNER_STATUSES = ["not_signed", "signed"] as const;
export type GiaSignerStatus = (typeof GIA_SIGNER_STATUSES)[number];

export function giaSignerStatusLabel(raw: unknown): string {
  const s = member(GIA_SIGNER_STATUSES, raw);
  return s === null
    ? "Status unavailable"
    : s === "signed"
      ? "Signed"
      : "Not signed";
}
